import React, { useState, useEffect } from 'react';
import { Save, Building2, Home, Plus, Minus, RotateCcw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import HouseEstimationCoefficients from './HouseEstimationCoefficients';

interface Coefficient {
  id: string;
  type: 'building' | 'apartment';
  name: string;
  value: number;
  description: string;
  default_value: number;
}

const MIN_VALUE = -20;
const MAX_VALUE = 20;
const STEP = 0.5;

export default function EstimationCoefficients() {
  const [coefficients, setCoefficients] = useState<Coefficient[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [activeTab, setActiveTab] = useState<'apartment' | 'building' | 'house'>('apartment');

  useEffect(() => {
    if (activeTab !== 'house') {
      fetchCoefficients();
    }
  }, [activeTab]);

  const fetchCoefficients = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/apartment-building-coefficients');
      
      if (!response.ok) {
        throw new Error('Failed to fetch apartment/building coefficients');
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch apartment/building coefficients');
      }
      
      setCoefficients(result.data || []);
    } catch (error) {
      console.error('Error fetching coefficients:', error);
      toast.error('Erreur lors du chargement des coefficients');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (id: string, increment: boolean) => {
    setCoefficients(prev =>
      prev.map(coef => {
        if (coef.id === id) {
          const newValue = increment 
            ? Math.min(coef.value + STEP, MAX_VALUE)
            : Math.max(coef.value - STEP, MIN_VALUE);
          return { ...coef, value: Number(newValue.toFixed(1)) };
        }
        return coef;
      })
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/apartment-building-coefficients/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ coefficients }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update coefficients');
      }

      toast.success('Coefficients mis à jour avec succès');
    } catch (error) {
      console.error('Error saving coefficients:', error);
      toast.error('Erreur lors de la sauvegarde des coefficients');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const response = await fetch('/api/apartment-building-coefficients/reset', {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset coefficients');
      }

      const result = await response.json();
      setCoefficients(result.data || []);
      toast.success('Coefficients réinitialisés avec succès');
    } catch (error) {
      console.error('Error resetting coefficients:', error);
      toast.error('Erreur lors de la réinitialisation des coefficients');
    } finally {
      setResetting(false);
    }
  };

  if (loading && activeTab !== 'house') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const buildingCoefs = coefficients.filter(c => c.type === 'building');
  const apartmentCoefs = coefficients.filter(c => c.type === 'apartment');

  const renderCoefficient = (coef: Coefficient) => (
    <div key={coef.id} className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {coef.description}
      </label>
      <div className="flex items-center space-x-2">
        <button
          type="button"
          onClick={() => handleValueChange(coef.id, false)}
          disabled={coef.value <= MIN_VALUE}
          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Minus className="h-4 w-4" />
        </button>
        <div className="w-20 text-center py-2 bg-gray-50 rounded-lg font-medium">
          {coef.value}
        </div>
        <button
          type="button"
          onClick={() => handleValueChange(coef.id, true)}
          disabled={coef.value >= MAX_VALUE}
          className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('apartment')}
            className={`${
              activeTab === 'apartment'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            <Building2 className="h-5 w-5 mr-2" />
            Coefficients Appartement
          </button>

          <button
            onClick={() => setActiveTab('building')}
            className={`${
              activeTab === 'building'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            <Building2 className="h-5 w-5 mr-2" />
            Coefficients Immeuble
          </button>

          <button
            onClick={() => setActiveTab('house')}
            className={`${
              activeTab === 'house'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            <Home className="h-5 w-5 mr-2" />
            Coefficients Maison
          </button>
        </nav>
      </div>

      {activeTab === 'house' ? (
        <HouseEstimationCoefficients />
      ) : (
        <>
          {/* Building Coefficients */}
          {activeTab === 'building' && (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div className="flex items-center">
                  <Building2 className="h-6 w-6 text-indigo-600 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    Coefficients Immeuble
                  </h2>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {buildingCoefs.map(renderCoefficient)}
                </div>
              </div>
            </div>
          )}

          {/* Apartment Coefficients */}
          {activeTab === 'apartment' && (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div className="flex items-center">
                  <Home className="h-6 w-6 text-indigo-600 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-900">
                    Coefficients Appartement
                  </h2>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {apartmentCoefs.map(renderCoefficient)}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <button
              onClick={handleReset}
              disabled={resetting}
              className="flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {resetting ? 'Réinitialisation...' : 'Remettre valeurs par défaut'}
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}